import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { i18n } from '../../i18n/translate';
import { Icon, Layout, DropdownMenu, Typography } from '../../core';
import { Button } from '../Button';
import { LanguageModal } from '../Modals/LanguageModal';
import { SchoolModal } from '../Modals/SchoolModal';
import { HelpModal } from '../Modals/HelpModal';
import { ConfirmationModal } from '../ConfirmationModal';
import { WindowHelperWrapper } from '../../utils/WindowHelper';
import { getActiveRuleSetFromUrl } from '../../utils/UrlUtil';
import { formatClassName } from '../../utils';
import { HeaderMenuAppSwitcher } from '../HeaderMenuAppSwitcher';
import { MenuButton as ChakraUIMenuButton } from '@chakra-ui/react';
import { HeaderMenuProps, MenuItem, SubmenuItem } from './types';

const EXPANDED_HEADER_BREAKPOINT = 768;

export const HeaderMenu: React.FC<HeaderMenuProps> = ({
  menuData,
  additionalNavClass = '',
  adminifiDomain,
  appendLanguage = false,
  currentRuleSets = [],
  helpMessage,
  helpMessageEnabled = false,
  hideNameOnMobile = true,
  homeroomDomain,
  isFoundry = false,
  isHomeroom = false,
  isImpersonating = false,
  isMobile: propsIsMobile = false,
  languages,
  onStopImpersonation,
  organizationSlug,
  pullRight = true,
  selectedLanguage = 'en',
  showCloseButton = true,
  user = null,
  windowOverride,
}) => {
  const serverSide = typeof window === 'undefined';
  const windowHelper = useMemo(() => WindowHelperWrapper(windowOverride), [windowOverride]);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showSchoolModal, setShowSchoolModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [isMobile, setIsMobile] = useState(serverSide && propsIsMobile);
  const [redirectRuleSetName, setRedirectRuleSetName] = useState<string | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

  const initialDropdowns = useMemo(() => {
    const dropdowns: Record<string, boolean> = {};
    menuData.submenu_items.forEach((item, key) => {
      if (item.submenu_items && item.submenu_items.length) {
        dropdowns[`headerMenu-${key}`] = false;
      }
    });
    return dropdowns;
  }, [menuData.submenu_items]);

  const [dropdowns, setDropdowns] = useState<Record<string, boolean>>(initialDropdowns);

  const checkHeader = useCallback((fullWidth: boolean) => {
    setIsMobile(!fullWidth);
  }, []);

  useEffect(() => {
    if (!serverSide) {
      // @ts-ignore
      const destroyWindowWatcher = windowHelper.watchBreakpoints(EXPANDED_HEADER_BREAKPOINT, checkHeader);
      return () => {
        destroyWindowWatcher();
      };
    }
  }, [serverSide, windowHelper, checkHeader]);

  const showMenuItem = useCallback(
    (item: MenuItem | SubmenuItem) => {
      return item.slug !== 'logout' || !isImpersonating;
    },
    [isImpersonating]
  );

  const getMenuLabel = useCallback(
    (item: MenuItem | SubmenuItem) => {
      const prefix = item.css_class === 'account' && isImpersonating ? i18n.t('Impersonating ') : '';
      return `${prefix}${item.title}`;
    },
    [isImpersonating]
  );

  const toggleLanguageModal = useCallback(() => {
    setShowLanguageModal(prev => !prev);
  }, []);

  const toggleSchoolModal = useCallback(() => {
    setShowSchoolModal(prev => !prev);
  }, []);

  const toggleHelpModal = useCallback(() => {
    setShowHelpModal(prev => !prev);
  }, []);

  const toggleConfirmationModal = useCallback(() => {
    if (selectedLanguage !== 'en') {
      window.location.reload();
    } else {
      goBackToAdminPortal();
    }
  }, [selectedLanguage]);

  const goBackToAdminPortal = useCallback(() => {
    let url = redirectUrl;
    if (adminifiDomain) {
      url = `${adminifiDomain}/en/${organizationSlug}/cportal/reports`;
      if (redirectRuleSetName) url = `${url}?current_rule_set_name=${redirectRuleSetName}`;
    }
    if (url) {
      windowHelper.redirect(url);
    }
  }, [adminifiDomain, organizationSlug, redirectRuleSetName, redirectUrl, windowHelper]);

  const onToggleDropdown = useCallback((open: boolean, id: string, event?: any) => {
    if (event?.source === 'rootClose') {
      setDropdowns({});
    } else {
      toggleDropdown(id, open);
    }
  }, []);

  const toggleDropdown = useCallback((key: string, open: boolean) => {
    setDropdowns(prev => ({ ...prev, [key]: open }));
  }, []);

  const buttonOrLink = useCallback(
    (item: MenuItem | SubmenuItem, target_url: string, isDropdown = false) => {
      const buttonClick: Record<string, any> = {};

      if (target_url === 'language_modal') {
        buttonClick.onClick = toggleLanguageModal;
      } else if (target_url === 'school_modal') {
        buttonClick.onClick = toggleSchoolModal;
      } else if (target_url === 'help_modal') {
        buttonClick.onClick = toggleHelpModal;
      } else if (target_url === 'confirmation_modal') {
        buttonClick.onClick = toggleConfirmationModal;
      } else if (item.slug === 'logout') {
        buttonClick.onClick = onStopImpersonation;
        buttonClick.href = target_url;
      } else {
        buttonClick.href = target_url;
        buttonClick.target = item.new_window ? '_blank' : '_self';
        if (isDropdown) {
          buttonClick['data-method'] = item.target_method && !isImpersonating ? item.target_method : 'GET';
        }
      }
      return buttonClick;
    },
    [
      toggleLanguageModal,
      toggleSchoolModal,
      toggleHelpModal,
      toggleConfirmationModal,
      onStopImpersonation,
      isImpersonating,
    ]
  );

  const renderLanguageModal = useCallback(() => {
    return (
      <LanguageModal
        renderAsLegacy={!isHomeroom}
        appendLanguage={!!appendLanguage}
        showLanguageModal={showLanguageModal}
        toggleLanguageModal={toggleLanguageModal}
        // @ts-ignore
        languages={languages}
        selectedLanguage={selectedLanguage}
      />
    );
  }, [isHomeroom, appendLanguage, showLanguageModal, toggleLanguageModal, languages, selectedLanguage]);

  const renderSchoolModal = useCallback(() => {
    return (
      <SchoolModal
        homeroomDomain={homeroomDomain}
        showSchoolModal={showSchoolModal}
        toggleSchoolModal={toggleSchoolModal}
        user={user}
      />
    );
  }, [homeroomDomain, showSchoolModal, toggleSchoolModal, user]);

  const renderHelpModal = useCallback(() => {
    return (
      <HelpModal
        helpMessage={helpMessage || ''}
        helpMessageEnabled={helpMessageEnabled}
        isFoundry={isFoundry}
        isHomeroom={isHomeroom}
        showHelpModal={showHelpModal}
        toggleHelpModal={toggleHelpModal}
        // @ts-ignore
        user={user}
      />
    );
  }, [helpMessage, helpMessageEnabled, isFoundry, showHelpModal, toggleHelpModal, user]);

  const renderConfirmationModal = useCallback(() => {
    return (
      <ConfirmationModal
        onConfirm={goBackToAdminPortal}
        onCancel={toggleConfirmationModal}
        show={showConfirmationModal}
      >
        {i18n.t(
          'You have selected to switch to the Admin Dashboard. The Admin Dashboard currently supports English only.'
        )}
      </ConfirmationModal>
    );
  }, [goBackToAdminPortal, toggleConfirmationModal, showConfirmationModal]);

  const runSwitch = useCallback(
    (redirectUrl: string) => {
      const url = new URL(redirectUrl);
      const current_rule_set_name = url.searchParams.get('current_rule_set_name');
      if (current_rule_set_name === 'cc_admin' && selectedLanguage !== 'en') {
        setShowConfirmationModal(true);
        setRedirectRuleSetName(current_rule_set_name);
        setRedirectUrl(redirectUrl);
      } else {
        windowHelper.redirect(redirectUrl);
      }
    },
    [selectedLanguage, windowHelper]
  );

  const renderDropdownMenuItems = useCallback(
    (menuItem: MenuItem, menuItemElements: any[] = []) => {
      if (!menuItem.submenu_items) return menuItemElements;

      menuItem.submenu_items.forEach((item, subKey) => {
        if (item.subsection) {
          if (subKey) {
            menuItemElements.push({ isDivider: true });
          }
          renderDropdownMenuItems(item as MenuItem, menuItemElements);
          return;
        }

        const adminDashboardLink = user && item.target_url === `${adminifiDomain}/${organizationSlug}/cportal/reports`;
        const href = adminDashboardLink ? 'confirmation_modal' : item.target_url;

        const onClick = () => {
          if (href === 'help_modal') return toggleHelpModal();
          if (href === 'language_modal') return toggleLanguageModal();
          if (href === 'school_modal') return toggleSchoolModal();
          if (href === 'confirmation_modal') return toggleConfirmationModal();

          if (item.slug === 'logout') {
            onStopImpersonation?.();
            if (href) window.location.href = href;
            return;
          }

          if (href) window.open(href, item.new_window ? '_blank' : '_self');
        };

        if (showMenuItem(item)) {
          menuItemElements.push({
            text: item.title,
            onClick,
            itemConfig: {
              iconName: item.icon,
              iconSize: 'lg',
              description: item.sub_text,
            },
          });
        }
      });

      return menuItemElements;
    },
    [
      user,
      adminifiDomain,
      organizationSlug,
      toggleHelpModal,
      toggleLanguageModal,
      toggleSchoolModal,
      toggleConfirmationModal,
      onStopImpersonation,
      showMenuItem,
    ]
  );

  const renderDropdown = useCallback(
    (menuItem: MenuItem, key: number) => {
      if (menuItem.slug === 'app-switcher') {
        const hasActiveRuleSet = getActiveRuleSetFromUrl(currentRuleSets);
        if (hasActiveRuleSet?.name) {
          return (
            <HeaderMenuAppSwitcher
              // @ts-ignore
              currentRuleSets={currentRuleSets}
              // @ts-ignore
              menuItems={menuItem.submenu_items || []}
              onRedirect={runSwitch}
            />
          );
        } else {
          return null;
        }
      }

      const isAccount = menuItem.css_class === 'account';
      const items = renderDropdownMenuItems(menuItem);

      const menuProps = {
        menuButton: (
          <Button
            // @ts-ignore
            as={ChakraUIMenuButton}
            rounded="full"
            variant="solid"
            colorScheme="neutral"
            size="compact"
            aria-label={getMenuLabel(menuItem)}
            {...(!isAccount &&
              menuItem.icon && {
                leftIcon: <Icon iconName={menuItem.icon} />,
              })}
            {...(isAccount && {
              rightIcon: 'bars',
            })}
          >
            {getMenuLabel(menuItem)}
          </Button>
        ),
        items,
      };
      return <DropdownMenu key={key} {...menuProps} />;
    },
    [currentRuleSets, runSwitch, renderDropdownMenuItems, getMenuLabel]
  );

  const renderLink = useCallback(
    (menuItem: MenuItem, key: number) => {
      const buttonClick = buttonOrLink(menuItem, menuItem.target_url);
      return (
        <Button
          id={`headerMenu-${key}`}
          rounded="full"
          variant="solid"
          colorScheme="neutral"
          size="compact"
          label={menuItem.title}
          title={menuItem.title}
          aria-label={menuItem.title}
          tabIndex={0}
          data-action={menuItem.title.toLowerCase()}
          // @ts-ignore
          icon={menuItem.icon}
          {...buttonClick}
        />
      );
    },
    [buttonOrLink]
  );

  const renderChildren = useCallback(() => {
    return menuData.submenu_items.map((item, key) => {
      return (
        <li key={key}>
          {item.submenu_items && item.submenu_items.length ? renderDropdown(item, key) : renderLink(item, key)}
        </li>
      );
    });
  }, [menuData.submenu_items, renderDropdown, renderLink]);

  const setOpenDropdownClass = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const openDropdownClass = 'header-menu-dropdown-open';
    const body = document.querySelector('body');
    const anyDropdownsOpen = Object.keys(dropdowns).length && Object.values(dropdowns).some(val => !!val);
    const bodyHasOpenClass = body?.classList.contains(openDropdownClass);
    if (anyDropdownsOpen && !bodyHasOpenClass) {
      body?.classList.add(openDropdownClass);
    } else if (!anyDropdownsOpen && bodyHasOpenClass) {
      body?.classList.remove(openDropdownClass);
    }
  }, [dropdowns]);

  setOpenDropdownClass();

  const classNames = formatClassName([
    'header-menu',
    { 'mobile-hide-name': hideNameOnMobile },
    { 'pull-right': pullRight },
    additionalNavClass,
  ]);

  return (
    <nav className={classNames} aria-label={i18n.t('Header Menu')}>
      <Layout as={'ul'} variant={'flex'}>
        {renderChildren()}
      </Layout>
      {renderLanguageModal()}
      {renderSchoolModal()}
      {renderHelpModal()}
      {renderConfirmationModal()}
    </nav>
  );
};
